import {
  ArrowDownIcon,
  ArrowUpIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import React, { useState, useEffect, Fragment, useRef } from "react";
import { createPortal } from "react-dom";
import {
  City,
  QuarantineCities,
  QuarantineCompany,
  QuarantineUniversity,
  State,
} from "../../../Models";
import {
  isQuarantineCompany,
  isQuarantineUniversity,
  isQuarantineCity,
} from "../../../Helpers";
import { QuarantineDataType, Sort } from "../Quarantine";
import AbbreviationModal from "../SubComponents/AbbreviationModal";
import { useNotification } from "../../../context/NotificationContext";
import ReactTooltip from "../../Shared/ReactToolTip";
import ConfirmModal from "../../Shared/ConfirmModal";
import RejectModal from "../SubComponents/RejectModal";
import MajorCityConfirmationPopup from "./MajorCityConfirmationPopup";
import SubCityConfirmationPopup from "./SubCityConfirmationPopup";
import RejectAndReplacePopup from "./RejectAndReplacePopup";
import { Filters } from "../../../api/config";
import {
  confirmQuarantineCompany,
  confirmQuarantineUniversity,
  deleteQuarantineCompany,
  deleteQuarantineUniversity,
  fetchCompanyLocations,
  rejectAndReplaceCities,
  updateQuarantineCity,
} from "../../../api/quarantine.api";
import {
  fetchConfirmedUniversities,
  GetCities,
  GetMajorCities,
} from "../../../api/masterdata.api";
import { MakeMajorCity, MakeSubcity } from "../../../api/location.api";
import { getUniversityURLsUsingGPT } from "../../../api/ai.api";
import AlternateNameConfirmationPopup from "../SubComponents/AlternateNameConfirmationPopup";
import PartitionConfirmModal, { ConsolidateItem } from "../SubComponents/PartitionConfirmModal";
import { consolidatePartition } from "../../../api/quarantine.api";

// ============================================================================
// TYPES & CONFIGURATION
// ============================================================================

const TABS = ["Companies", "Universities", "Cities"] as const;
type TabType = (typeof TABS)[number];

interface HeaderProps {
  quarantineData?: QuarantineDataType[];
  sortOption: Sort | undefined;
  setSortOption: React.Dispatch<React.SetStateAction<Sort | undefined>>;
  selectedRows: QuarantineDataType[];
  setSelectedRows: React.Dispatch<React.SetStateAction<QuarantineDataType[]>>;
  token: string;
  selected: string;
  setQuarantineData: React.Dispatch<React.SetStateAction<QuarantineDataType[]>>;
  fetchQuarantineData: (
    sortType?: string,
    sortDesc?: boolean,
    Filters?: Filters[],
  ) => Promise<void>;
  toggleRow: (data: QuarantineUniversity) => void;
  selectedCompanyLocations: string[];
  setSelectedCompanyLocations: React.Dispatch<React.SetStateAction<string[]>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  states: State[];
  showConfirmed: boolean;
  setShowRejectModalParent: React.Dispatch<React.SetStateAction<boolean>>;
  setSubCityClickPopUpParent: React.Dispatch<React.SetStateAction<boolean>>;
  setMajorCityClickPopUpParent: React.Dispatch<React.SetStateAction<boolean>>;
  setAlternateNameClickPopUpParent: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  showRejectModalParent: boolean;
  subCityClickPopUpParent: boolean;
  majorCityClickPopUpParent: boolean;
  alternateNameClickPopUpParent: boolean;
  showConfirmedCities: boolean;
  gptCityDetails: any;
  getgptcitydetails: (cityId: string, details: any) => void;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  urlValidationResults: Record<string, { isReachable: boolean; statusCode: number; message: string } | 'loading'>;
}

// ============================================================================
// HEADER COLUMN CONFIGURATION
// ============================================================================

interface HeaderColumnConfig {
  showURL: boolean;
  showCount: boolean;
  showCity: boolean;
  showName: boolean;
  showCityName: boolean;
  showType: boolean;
  showAction: boolean;
  showTags: boolean;
  showState: boolean;
  showNearestCity: boolean;
  showUpdateURLButton: boolean;
  sortableColumns: string[];
  actionButtons: "company-university" | "cities";
}

const HEADER_CONFIGS: Record<TabType, HeaderColumnConfig> = {
  Companies: {
    showURL: true,
    showCount: true,
    showCity: true,
    showName: true,
    showCityName: false,
    showType: false,
    showAction: false,
    showTags: true,
    showState: false,
    showNearestCity: false,
    showUpdateURLButton: false,
    sortableColumns: ["Count", "Name"],
    actionButtons: "company-university",
  },
  Universities: {
    showURL: true,
    showCount: true,
    showCity: true,
    showName: true,
    showCityName: false,
    showType: false,
    showAction: false,
    showTags: true,
    showState: false,
    showNearestCity: false,
    showUpdateURLButton: false,
    sortableColumns: ["Count", "Name"],
    actionButtons: "company-university",
  },
  Cities: {
    showURL: false,
    showCount: true,
    showCity: false,
    showName: false,
    showCityName: true,
    showType: true,
    showAction: true,
    showTags: false,
    showState: true,
    showNearestCity: true,
    showUpdateURLButton: false,
    sortableColumns: ["Name", "Count", "State"],
    actionButtons: "cities",
  },
};

// ============================================================================
// REUSABLE HEADER COLUMN TEMPLATES
// ============================================================================

// Sort Icon Component
const SortIcon: React.FC<{
  columnName: string;
  sortOption: Sort | undefined;
  onSort: (column: string) => void;
}> = ({ columnName, sortOption, onSort }) => {
  const isActive = sortOption?.name === columnName;
  const iconClass = `w-4 cursor-pointer ml-1 ${isActive ? "text-white" : "text-gray-400"}`;

  return isActive && sortOption.dsc ? (
    <ArrowDownIcon
      className={iconClass}
      onClick={() => onSort(columnName)}
    />
  ) : (
    <ArrowUpIcon
      className={iconClass}
      onClick={() => onSort(columnName)}
    />
  );
};

// Header Column with Sort
const SortableHeaderColumn: React.FC<{
  label: string;
  columnName: string;
  sortOption: Sort | undefined;
  onSort: (column: string) => void;
  showInfo?: boolean;
  tooltipId?: string;
}> = ({ label, columnName, sortOption, onSort, showInfo, tooltipId }) => {
  const isActive = sortOption?.name === columnName;
  return (
    <th className="py-2 px-3">
      <div className="flex items-center">
        <span
          className={`inline-flex items-center ${
            isActive
              ? "bg-entntblue text-white font-semibold rounded-full px-2 py-0.5"
              : ""
          }`}
        >
          <span>{label}</span>
          <SortIcon
            columnName={columnName}
            sortOption={sortOption}
            onSort={onSort}
          />
        </span>
        {showInfo && tooltipId && (
          <InformationCircleIcon
            data-tooltip-id={tooltipId}
            className="w-6 h-6 ml-2"
          />
        )}
      </div>
    </th>
  );
};

// Simple Header Column
const HeaderColumn: React.FC<{
  label: string;
  showInfo?: boolean;
  tooltipId?: string;
}> = ({ label, showInfo, tooltipId }) => (
  <th className="py-2 px-3">
    <div className="flex items-center">
      {label}
      {showInfo && tooltipId && (
        <InformationCircleIcon
          data-tooltip-id={tooltipId}
          className="w-6 h-6 ml-2"
        />
      )}
    </div>
  </th>
);

// Action Buttons for Companies/Universities
const CompanyUniversityActions: React.FC<{
  selected: string;
  selectedRows: any[];
  onConfirmInCity: () => void;
  onRejectReplace: () => void;
}> = ({ selected, selectedRows, onConfirmInCity, onRejectReplace }) =>
  selectedRows.length > 0 ? (
    <div className="flex pt-1">
      <button
        onClick={onConfirmInCity}
        className="absolute top-[0.2rem] left-[4rem] py-2 px-3 rounded-md text-white bg-entntblue"
      >
        Confirm {selected === "Companies" ? "Company" : "University"} in City
      </button>
      <button
        onClick={onRejectReplace}
        className="absolute top-[0.2rem] left-[15rem] py-2 px-3 rounded-md text-white bg-entntblue"
      >
        Reject and Replace
      </button>
    </div>
  ) : null;

// Action Buttons for Cities
const CityActions: React.FC<{
  selectedRows: any[];
  onConfirmMajor: () => void;
  onConfirmSub: () => void;
  onConfirmAlternate: () => void;
  onRejectReplace: () => void;
}> = ({
  selectedRows,
  onConfirmMajor,
  onConfirmSub,
  onConfirmAlternate,
  onRejectReplace,
}) =>
  selectedRows.length > 0 ? (
    <div className="flex flex-wrap pt-1">
      <button
        onClick={onConfirmMajor}
        className="absolute top-[0.2rem] left-[4rem] py-2 px-3 rounded-md text-white bg-entntblue"
      >
        Confirm As Major City
      </button>
      <button
        onClick={onConfirmSub}
        className="absolute top-[0.2rem] left-[14rem] py-2 px-3 rounded-md text-white bg-entntblue"
      >
        Confirm As Sub City
      </button>
      <button
        onClick={onConfirmAlternate}
        className="absolute top-[0.2rem] left-[23.2rem] py-2 px-3 rounded-md text-white bg-entntblue"
      >
        Confirm As Alternate Name
      </button>
      <button
        onClick={onRejectReplace}
        className="absolute top-[0.2rem] left-[35rem] py-2 px-3 rounded-md text-white bg-red-500"
      >
        Reject And Replace
      </button>
    </div>
  ) : null;

// Update URL Button for Universities
const UpdateURLButton: React.FC<{
  loading: boolean;
  urlUpdateSuccess: boolean;
  onClick: () => void;
}> = ({ loading, urlUpdateSuccess, onClick }) => (
  <th className="py-2 px-3">
    <div className="flex items-center gap-2">
      {/* <button
        className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition flex items-center gap-1"
        onClick={onClick}
        disabled={loading}
      >
        {loading ? (
          <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full inline-block" />
        ) : urlUpdateSuccess ? (
          <span className="text-green-300">✔️</span>
        ) : null}
        Update University URLs
      </button> */}
    </div>
  </th>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function TableHeader({
  quarantineData,
  sortOption,
  setSortOption,
  selectedRows,
  setSelectedRows,
  token,
  selected,
  setQuarantineData,
  fetchQuarantineData,
  toggleRow,
  selectedCompanyLocations,
  setSelectedCompanyLocations,
  setPage,
  states,
  showConfirmed,
  setShowRejectModalParent,
  setSubCityClickPopUpParent,
  setMajorCityClickPopUpParent,
  setAlternateNameClickPopUpParent,
  showRejectModalParent,
  subCityClickPopUpParent,
  majorCityClickPopUpParent,
  alternateNameClickPopUpParent,
  showConfirmedCities,
  gptCityDetails,
  getgptcitydetails,
  pageNumber,
  setPageNumber,
  urlValidationResults,
}: HeaderProps) {
  // Get current header configuration
  const headerConfig = HEADER_CONFIGS[selected as TabType];

  // State Management
  const [headCheckboxState, setHeadCheckboxState] = useState<boolean | "minus">(
    false,
  );
  const [showConfirmBox, setShowConfirmBox] = useState<boolean>(false);
  const [showAbbBox, setShowAbbBox] = useState<boolean>(false);
  const [openConfirmationBox, setOpenConfirmationBox] =
    useState<boolean>(false);
  const [OpenCityConfirmBox, setOpenCityConfirmBox] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const { success: notifySuccess, error: notifyError } = useNotification();
  const [confirmedData, setConfirmedData] = useState<
    QuarantineUniversity[] | QuarantineCompany[]
  >();
  const [searchValue, setSearchValue] = useState("");
  const [cities, setCities] = useState<City[]>([]);
  const [majorCities, setMajorCities] = useState<City[]>([]);
  const [majorCityId, setMajorCityId] = useState<string | null>();
  const [filteredOptions, setFilteredOptions] = useState<City[]>([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const [loading, setLoading] = useState(false);
  const [urlUpdateSuccess, setUrlUpdateSuccess] = useState(false);

  // Partition Modal State
  const [showPartitionModal, setShowPartitionModal] = useState(false);
  const [partitionModalData, setPartitionModalData] = useState<ConsolidateItem[]>([]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (quarantineData && selectedRows.length === quarantineData.length) {
      setHeadCheckboxState(true);
    } else if (selectedRows.length > 0) {
      setHeadCheckboxState("minus");
    } else {
      setHeadCheckboxState(false);
    }
  }, [selectedRows, quarantineData]);

  useEffect(() => {
    fetchConfirmedData();
  }, [selected]);

  useEffect(() => {
    GetMajorCities(token).then((c) => {
      setMajorCities(c);
    });
    GetCities(token).then((c) => {
      setCities(c);
    });
  }, []);

  // Handler for Reject popup - extracts GPT details for all selected cities
  const handleOpenRejectPopup = () => {
    selectedRows.forEach((city) => {
      if (!isQuarantineCity(city)) return;
      const action = city?.actiongpt?.action;

      if (
        action === "Already Exists" ||
        action === "City exist with different name"
      ) {
        getgptcitydetails(city.id, {
          corrected_name: city.actiongpt.matched_city,
        });
      }

      if (action === "Invalid City") {
        getgptcitydetails(city.id, {
          corrected_name: "Invalid",
        });
      }
    });
  };

  // Handler for SubCity popup - extracts GPT details for all selected cities
  const handleOpenSubCityPopup = () => {
    selectedRows.forEach((city) => {
      if (!isQuarantineCity(city)) return;
      const action = city?.actiongpt?.action?.toLowerCase();

      if (action === "valid city" && !showConfirmed) {
        getgptcitydetails(city.id, {
          major_city: city.actiongpt.major_city,
        });
      }

      if (action === "typo detected" && city.actiongpt.major_city !== "null") {
        getgptcitydetails(city.id, {
          corrected_name: city.actiongpt.corrected_name,
          major_city: city.actiongpt.major_city,
          Actual_state: city.actiongpt.actual_state,
        });
      }

      if (action === "state mismatch") {
        getgptcitydetails(city.id, {
          major_city: "",
          correctedStateId: city.actiongpt.correctedStateId,
        });
      }
    });
  };

  // Handler for MajorCity popup - extracts GPT details for all selected cities
  const handleOpenMajorCityPopup = () => {
    selectedRows.forEach((city) => {
      if (!isQuarantineCity(city)) return;
      const action = city?.actiongpt?.action?.toLowerCase();

      if (action === "typo detected" && city.actiongpt.major_city === "null") {
        getgptcitydetails(city.id, {
          corrected_name: city.actiongpt.corrected_name,
        });
      }
    });
  };

  // Clear GPT details when all popups are closed
  useEffect(() => {
    if (
      !showRejectModalParent &&
      !subCityClickPopUpParent &&
      !majorCityClickPopUpParent
    ) {
      getgptcitydetails("", {});
    }
  }, [
    showRejectModalParent,
    subCityClickPopUpParent,
    majorCityClickPopUpParent,
  ]);

  useEffect(() => {
    if (searchValue === "") {
      setFilteredOptions([]);
      return;
    }
    const results = majorCities.filter((option) =>
      option.name.toLowerCase().includes(searchValue.toLowerCase()),
    );
    setFilteredOptions(results);
  }, [searchValue, cities, majorCities]);

  useEffect(() => {
    setFilteredOptions([]);
  }, [selectedOption]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const initialSortOptions: Sort[] = [
    { name: "Name", dsc: true },
    { name: "State", dsc: true },
    { name: "City", dsc: true },
    { name: "Count", dsc: true },
  ];

  const fetchConfirmedData = async (name?: string) => {
    if (selected === TABS[0]) {
      try {
        const comp = await fetchCompanyLocations(name, token);
        if (comp) {
          setConfirmedData(comp);
        }
      } catch (err) {
        console.log(err);
      }
    } else if (selected === TABS[1]) {
      try {
        const uni = await fetchConfirmedUniversities(name, 1, 500, "");
        if (uni) {
          setConfirmedData(uni);
        } else console.error("Token is undefined or empty.");
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleTableSort = async (sortType: string) => {
    if (!quarantineData) {
      return;
    }
    if (sortOption && sortOption.name === sortType) {
      const newSortOption = { name: sortOption.name, dsc: !sortOption.dsc };
      setSortOption(newSortOption);
    } else {
      const newSortOption = initialSortOptions.find((s) => s.name === sortType);
      setSortOption(newSortOption);
    }
  };

  const toggleAllRows = () => {
    if (headCheckboxState === true || headCheckboxState === "minus") {
      setSelectedRows([]);
    } else {
      // Add logic to select all rows if needed
    }
  };

  const deleteInstitute = async (quarantineId: string, confirmedId: string) => {
    try {
      if (selected === TABS[1]) {
        const message = await deleteQuarantineUniversity(
          token,
          quarantineId,
          confirmedId,
        );
        if (message)
          notifySuccess(
            "Rejection Successful",
            "University Rejected Successfully.",
          );
      } else {
        const message = await deleteQuarantineCompany(
          token,
          quarantineId,
          confirmedId,
        );
        if (message)
          notifySuccess(
            "Rejection Successful",
            "Company Rejected Successfully.",
          );
      }
      setPage(1);
      setQuarantineData([]);
      await fetchQuarantineData();
    } catch (error) {
      console.error("Failed to delete university:", error);
      notifyError("Error", "Failed to delete university.");
    }
    setShowRejectModal(false);
  };

  const handleConfirm = async (newId: string, abbreviation?: string, skipRefresh: boolean = false) => {
    const selectedInstitute = selectedRows.find((institute: any) =>
      selected === TABS[0]
        ? institute.companyLocations!.find(
            (location: any) => location.id === newId,
          )
        : institute.urlGroups!.flatMap((g: any) => g.universities || []).find((u: any) => u.id === newId),
    );
    if (!selectedInstitute) {
      return;
    }
    const Data = {
      Id: newId,
      IsConfirmed: true,
      IsRejected: false,
      Abbreviation: abbreviation,
    };
    try {
      if (selected === TABS[1]) {
        const message = await confirmQuarantineUniversity(token, newId, Data);
      } else {
        const message = await confirmQuarantineCompany(token, newId, Data);
      }
      
      if (!skipRefresh) {
        setPage(1);
        setQuarantineData([]);
        await fetchQuarantineData();
        await fetchConfirmedData();
      }
    } catch (error) {
      console.error(`Failed to update ${selected}:, ${error}`);
    }
  };

  const prepareConsolidateItem = (row: any): ConsolidateItem => {
    // Identify Winner Group: use the one where at least one variant is selected, or fallback to first
    const winnerGroup = row.urlGroups.find((g: any) =>
      g.companyLocations?.some((cl: any) => !selectedCompanyLocations.includes(cl.id))
    ) || row.urlGroups[0];

    const allCityLocations = row.urlGroups.flatMap((g: any) =>
      (g.companyLocations || []).map((cl: any) => ({
        locationId: cl.id,
        cityId: cl.city.id,
        cityName: cl.city.name,
        candidateCount: cl.city.count,
        url: g.url,
      }))
    );

    const winnerLocationIds = winnerGroup?.companyLocations?.map((cl: any) => cl.id) || [];
    const winnerCompanyId = winnerGroup?.companyLocations?.[0]?.companyId || row.id || row.companyId;
    
    // Use allCompanyIds when available — it includes companies whose locations were hidden
    // by city-level deduplication in BuildLocationDtos (same URL, same city, different company record)
    const allLoserIds = row.urlGroups
      .flatMap((g: any) =>
        g.allCompanyIds?.length > 0
          ? g.allCompanyIds
          : (g.companyLocations || []).map((cl: any) => cl.companyId)
      )
      .filter((id: string) => id && id !== winnerCompanyId);
    
    const uniqueLoserIds = [...new Set(allLoserIds)] as string[];

    const validation = urlValidationResults[winnerGroup?.url || ""];
    const isWinnerUrlValid = validation && validation !== 'loading' ? (validation as any).isReachable !== false : true;

    return {
      companyId: winnerCompanyId,
      name: row.name,
      winnerUrl: winnerGroup.url,
      isWinnerUrlValid,
      allLocations: allCityLocations,
      winnerLocationIds,
      loserCompanyIds: uniqueLoserIds,
      batchKey: `${winnerCompanyId}-${row.name}`
    };
  };

  const handleMultiConfirm = async () => {
    try {
      if (selectedRows.length === 0) {
        setOpenConfirmationBox(false);
        return;
      }

      if (selected === TABS[0]) {
        // VALIDATION: Ensure only one URL group is selected per partition
        for (const row of selectedRows as any[]) {
          const selectedUrlGroups = row.urlGroups?.filter((g: any) => 
            g.companyLocations.some((cl: any) => !selectedCompanyLocations.includes(cl.id))
          ) || [];

          if (selectedUrlGroups.length > 1) {
            notifyError(
              "Selection Error",
              `Multiple URL variants selected for "${row.name}". Please select only one URL per company to confirm.`
            );
            return;
          }
        }

        // Prepare items for batch consolidation
        const batchItems = (selectedRows as any[]).map(row => prepareConsolidateItem(row));
        setPartitionModalData(batchItems);
        setShowPartitionModal(true);
        setOpenConfirmationBox(false);
      } else {
        // Universities logic remains simple for now
        for (const obj of selectedRows as any[]) {
          for (const g of obj.urlGroups || []) {
            for (const u of g.universities || []) {
              await handleConfirm(u.id, undefined, true);
            }
          }
        }
        setPage(1);
        setQuarantineData([]);
        await fetchQuarantineData();
        setSelectedRows([]);
        notifySuccess("Confirmation Successful", `${selected} Updated Successfully.`);
        setOpenConfirmationBox(false);
      }
    } catch (error) {
      console.error(`Failed to update ${selected}:, ${error}`);
      notifyError("Error", `Failed to update ${selected}.`);
    }
  };

  const handlePartitionConfirm = async (results: any[]) => {
    setLoading(true);
    try {
      // Process each consolidation in the batch
      for (const res of results) {
        const payload = {
          winnerCompanyId: res.winnerCompanyId,
          loserCompanyIds: res.loserCompanyIds,
          selectedCityIds: res.selectedCityIds,
          confirmedUrl: res.confirmedUrl
        };
        const apiRes = await consolidatePartition(payload, token);
      }

      notifySuccess("Batch Confirmation Successful", `Successfully processed ${results.length} companies.`);
      
      setShowPartitionModal(false);
      setSelectedRows([]);
      setSelectedCompanyLocations([]);
      setPage(1);
      setQuarantineData([]);
      await fetchQuarantineData();
    } catch (error: any) {
      console.error("Batch consolidation failed:", error);
      notifyError("Processing Error", error.message || "An error occurred during batch confirmation.");
    } finally {
      setLoading(false);
    }
  };

  const handleAbbrevationBox = async (updatedRows: any[]) => {
    try {
      for (const obj of updatedRows) {
        if (
          !isQuarantineUniversity(obj) ||
          !obj.abbreviation ||
          obj.abbreviation === ""
        ) {
          setShowAbbBox(false);
          notifyError(
            "Set Abbreviation",
            "Please Select an Abbreviation for all Universities",
          );
          return;
        }
      }
      for (const obj of updatedRows) {
        if (obj.abbreviation) await handleConfirm(obj.id, obj.abbreviation, true);
      }
      setPage(1);
      setQuarantineData([]);
      await fetchQuarantineData();
      setPage(1);
      setSelectedRows([]);
      setSelectedCompanyLocations([]);
      notifySuccess("Updation Successful", `${selected} Updated Successfully.`);
      setShowConfirmBox(false);
      setShowAbbBox(false);
    } catch (error) {
      console.error(`Failed to update ${selected}:, ${error}`);
      notifyError("Error", `Failed to update ${selected}.`);
    }
  };

  const handleMakeSubCity = async (
    majorCityId: string,
    replaceCommon: boolean[],
    updatedRows?: any[],
  ) => {
    const rowsToUse = updatedRows ?? selectedRows;
    if (!majorCityId || rowsToUse.length === 0) {
      return;
    }
    if (!isQuarantineCity(rowsToUse[0])) return;
    handleClosePopup();
    try {
      const cityRows = rowsToUse as QuarantineCities[];
      let updateObj = cityRows.map((sr) => {
        return {
          cityId: sr.id,
          stateId: sr.state.id,
          name: sr.name,
          alternateNames: sr.alternateNames,
        };
      });
      await updateQuarantineCity(updateObj, token);
      const cityIds = cityRows.map((city) => city.id);
      await MakeSubcity(cityIds, majorCityId, token);
      cityRows.map(async (s, index: number) => {
        if (s.replacedCities?.length !==0 && replaceCommon[index]) {
            const rejectedData: {
              cityId: string;
              replacedCityId: string | null;
            }[] = cityRows.flatMap(
              (s) =>
                s.replacedCities?.map((city) => ({
                  cityId: city.id,
                  replacedCityId: s.id,
                })) || []
            );
            await handleConfirmRejectCity(rejectedData, true);
        }
      });
      notifySuccess(
        "Confirmation Successful",
        `Cities confirmed as Sub City successfully.`,
      );
    } catch (error) {
      console.error("Error making subcity:", error);
      notifyError("Error", "Failed to confirm as sub city.");
    }
    setPage(1);
    setSelectedRows([]);
    fetchQuarantineData();
    setShowRejectModalParent(false);
  };

  const handleClosePopup = () => {
    setMajorCityClickPopUpParent(false);
    setSubCityClickPopUpParent(false);
    setSubCityClickPopUpParent(false);
    setMajorCityClickPopUpParent(false);
    setAlternateNameClickPopUpParent(false);
    setAlternateNameClickPopUpParent(false);
    setSearchValue("");
    setMajorCityId(null);
  };

  const handleRejectClosePopup = () => {
    setShowRejectModalParent(false);
  };

  const handleConfirmRejectCity = async (
    rejectedData: { cityId: string; replacedCityId: string | null }[],
    isSilent?: boolean
  ) => {
    await rejectAndReplaceCities(rejectedData, token).then(() => {
      setPage(1);
      setSelectedRows([]);
      fetchQuarantineData();
      setShowRejectModalParent(false);
      if(!isSilent){
        notifySuccess(
          "Rejection Successful",
          `${selected} Updated Successfully.`,
        );
      }
    });
  };

  const handleMakeMajorCity = async (replaceCommon: boolean[], updatedRows?: any[]) => {
    const rowsToUse = updatedRows ?? selectedRows;
    if (rowsToUse.length === 0) {
      return;
    }
    if (!isQuarantineCity(rowsToUse[0])) return;
    handleClosePopup();
    try {
      const cityRows = rowsToUse as QuarantineCities[];
      let updateObj = cityRows.map((sr) => {
        return {
          cityId: sr.id,
          stateId: sr.state.id,
          name: sr.name,
          alternateNames: sr.alternateNames,
        };
      });
      await updateQuarantineCity(updateObj, token);
      const cityIds = cityRows.map((city) => city.id);
      await MakeMajorCity(cityIds, token);
      cityRows.map(async (s, index: number) => {
        if (s.replacedCities?.length !== 0) {
          if (replaceCommon[index]) {
            const rejectedData: {
              cityId: string;
              replacedCityId: string | null;
            }[] = cityRows.flatMap(
              (s) =>
                s.replacedCities?.map((city) => ({
                  cityId: city.id,
                  replacedCityId: s.id,
                })) || []
            );
            await handleConfirmRejectCity(rejectedData, true);
          }
        }
      });
      notifySuccess(
        "Confirmation Successful",
        `Cities confirmed as Major City successfully.`,
      );
    } catch (error) {
      console.error("Error making major city:", error);
      notifyError("Error", "Failed to confirm as major city.");
    }

    setPage(1);
    setSelectedRows([]);
    fetchQuarantineData();
    setShowRejectModalParent(false);
    notifySuccess(
      "Confirmation Successful",
      `${selected} Updated Successfully.`,
    );
  };

  const handleConfirmAsAlternateName = async (
    mappings: {
      sourceCityId: string;
      sourceCityName: string;
      targetCityId: string;
    }[],
  ) => {
    try {
      // 1. Reuse RejectAndReplace to update all references of City1 -> City2
      //    and delete City1
      const replacePayload = mappings.map((m) => ({
        cityId: m.sourceCityId,
        replacedCityId: m.targetCityId,
      }));

      await rejectAndReplaceCities(replacePayload, token);

      // 2. Update alternateNames for each target city (City2)
      //    Group source names by target city
      const alternateUpdatesByTarget = new Map<string, string[]>();
      mappings.forEach(({ sourceCityName, targetCityId }) => {
        const arr = alternateUpdatesByTarget.get(targetCityId) || [];
        arr.push(sourceCityName);
        alternateUpdatesByTarget.set(targetCityId, arr);
      });

      // For each target city, find its current alternateNames and append
      for (const [targetCityId, altNames] of alternateUpdatesByTarget) {
        const targetCity = cities.find((c) => c.id === targetCityId);
        const existingAlternates = targetCity?.alternateNames || "";
        const newAlternates = existingAlternates
          ? `${existingAlternates},${altNames.join(",")}`
          : altNames.join(",");

        await updateQuarantineCity(
          [
            {
              cityId: targetCityId,
              stateId: targetCity?.state?.id || "",
              name: targetCity?.name || "",
              alternateNames: newAlternates,
            },
          ],
          token,
        );
      }

      // 3. Refresh UI
      setPage(1);
      setSelectedRows([]);
      await fetchQuarantineData();
      setAlternateNameClickPopUpParent(false);
      notifySuccess(
        "Successful",
        "Cities confirmed as alternate names successfully.",
      );
    } catch (error) {
      console.error("Error confirming as alternate name:", error);
      notifyError("Error", "Failed to confirm as alternate name.");
      setAlternateNameClickPopUpParent(false);
    }
  };

  const updateSelectedRows = (
    cityIndex: number,
    cityName: string,
    stateId: string,
    alternateNames: string,
  ) => {
    const updatedRows = selectedRows.map((city, index) => {
      if (index === cityIndex) {
        const newState = states.find((s) => s.id === stateId);
        return {
          ...city,
          name: cityName,
          state: newState || city.state,
          alternateNames: alternateNames,
        };
      }
      return city;
    });
    setSelectedRows(updatedRows);
  };

  const handleUpdateURLs = async () => {
    setLoading(true);
    setUrlUpdateSuccess(false);
    try {
      const result = await getUniversityURLsUsingGPT();
      setUrlUpdateSuccess(true);
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (err: any) {
      alert("Failed to update university URLs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const modals = (
    <>
      <AbbreviationModal
        type="info"
        open={showAbbBox}
        setOpen={setShowAbbBox}
        onConfirm={handleAbbrevationBox}
        title="Confirm Universities"
        description="Set Abbreviations for Universities and Confirm."
        selectedRows={selectedRows}
        selectedOptions={selectedCompanyLocations}
      />

      {showPartitionModal && (
        <PartitionConfirmModal
          isOpen={showPartitionModal}
          onClose={() => setShowPartitionModal(false)}
          onConfirm={handlePartitionConfirm}
          items={partitionModalData}
          isLoading={loading}
        />
      )}

      <RejectModal
        type="danger"
        open={showRejectModal}
        setOpen={setShowRejectModal}
        deleteInstitute={deleteInstitute}
        title={`Reject ${selected === "Companies" ? "Company" : "University"}`}
        description={`Choose a ${
          selected === "Companies" ? "Company" : "University"
        }`}
        dropDownPlaceHolder={
          selected === "Companies" ? "Companies" : "Universities"
        }
        dropDownValues={confirmedData ? confirmedData : []}
        hideOthers
        selected={selected}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        nonSelectedOptions={selectedCompanyLocations}
        fetchConfirmedData={fetchConfirmedData}
      />

      <ReactTooltip
        key="city"
        id="city"
        render={() => (
          <div className="flex flex-col gap-2 text-sm">
            <p>The number indicates how many times the city appeared across all Quarantine {selected === TABS[0] ? "Companies" : "Universities"}.</p>
            <div className="flex flex-col gap-1 mt-1">
              <span className="flex items-center gap-1.5 whitespace-nowrap"><span className="inline-block w-3 h-3 rounded bg-red-500 shrink-0" /> Confirmed city</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap"><span className="inline-block w-3 h-3 rounded shrink-0" style={{ backgroundColor: "#0D42EC" }} /> Unconfirmed city</span>
            </div>
            {selected === TABS[1] && (
              <div className="flex flex-col gap-1 border-t border-gray-600 pt-2">
                <span className="font-semibold whitespace-nowrap">URL:</span>
                <span className="whitespace-nowrap"><span className="text-red-400 font-medium">Red</span> — confirmed for another location</span>
                <span className="whitespace-nowrap"><span className="font-medium" style={{ color: "#6dacff" }}>Blue</span> — new URL</span>
              </div>
            )}
          </div>
        )}
      />
      <ReactTooltip
        key="URL"
        id="URL"
        content="URLs displayed in red indicate that a confirmed company with the same URL already exists in DB."
      />

      {subCityClickPopUpParent && (
        <SubCityConfirmationPopup
          isOpen={subCityClickPopUpParent}
          onCancel={handleClosePopup}
          onConfirm={handleMakeSubCity}
          selectedRows={selectedRows}
          majorCityOptions={majorCities}
          updateSelectedRows={updateSelectedRows}
          states={states}
          gptCityDetails={gptCityDetails}
          token={token}
        />
      )}

      {majorCityClickPopUpParent && (
        <MajorCityConfirmationPopup
          isOpen={majorCityClickPopUpParent}
          selectedRows={selectedRows}
          onCancel={handleClosePopup}
          isModalConfirm={handleMakeMajorCity}
          size="lg"
          states={states}
          updateSelectedRows={updateSelectedRows}
          gptCityDetails={gptCityDetails}
          token={token}
        />
      )}

      {showRejectModalParent && (
        <RejectAndReplacePopup
          isOpen={showRejectModalParent}
          onCancel={handleRejectClosePopup}
          onConfirm={handleConfirmRejectCity}
          selectedRows={selectedRows}
          cities={cities}
          showConfirmedCities={showConfirmedCities}
          gptCityDetails={gptCityDetails}
        />
      )}

      {alternateNameClickPopUpParent && (
        <AlternateNameConfirmationPopup
          isOpen={alternateNameClickPopUpParent}
          onCancel={handleClosePopup}
          onConfirm={handleConfirmAsAlternateName}
          selectedRows={selectedRows}
          cities={cities}
          states={states}
          updateSelectedRows={updateSelectedRows}
          gptCityDetails={gptCityDetails}
        />
      )}
    </>
  );

  return (
    <>
      {createPortal(modals, document.body)}
      <thead className="sticky top-16 text-xs text-gray-800 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400 z-50">
        <tr>
          {/* ... existing thead content ... */}
          {/* Checkbox Column - Always visible */}
          <th scope="col" className="w-[3rem]">
            <div className="py-2 px-5 flex">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100"
                checked={headCheckboxState === true}
                ref={(input) => {
                  if (input)
                    input.indeterminate = headCheckboxState === "minus";
                }}
                onChange={toggleAllRows}
              />
              <EllipsisVerticalIcon
                className="w-4 ml-2 cursor-pointer"
                onClick={() => setShowConfirmBox((prev) => !prev)}
              />

              {/* Dynamic Action Buttons based on tab */}
              {headerConfig.actionButtons === "company-university" ? (
                <CompanyUniversityActions
                  selected={selected}
                  selectedRows={selectedRows}
                  onConfirmInCity={() => {
                    if (selected === TABS[0]) {
                      handleMultiConfirm();
                    } else {
                      setShowAbbBox(true);
                    }
                  }}
                  onRejectReplace={() => setShowRejectModal(true)}
                />
              ) : (
                <CityActions
                  selectedRows={selectedRows}
                  onConfirmAlternate={() =>
                    setAlternateNameClickPopUpParent(true)
                  }
                  onConfirmMajor={() => {
                    handleOpenMajorCityPopup();
                    setMajorCityClickPopUpParent(true);
                  }}
                  onConfirmSub={() => {
                    handleOpenSubCityPopup();
                    setSubCityClickPopUpParent(true);
                  }}
                  onRejectReplace={() => {
                    handleOpenRejectPopup();
                    setShowRejectModalParent(true);
                  }}
                />
              )}
            </div>
          </th>

          {/* URL Column - Companies only */}
          {headerConfig.showURL && selected === TABS[0] && (
            <HeaderColumn label="URL" showInfo tooltipId="URL" />
          )}

          {/* URLs Column - Universities only */}
          {headerConfig.showURL && selected === TABS[1] && (
            <HeaderColumn label="URLs" />
          )}

          {/* Count Column */}
          {headerConfig.showCount && !headerConfig.showCityName && (
            <SortableHeaderColumn
              label="Count"
              columnName="Count"
              sortOption={sortOption}
              onSort={handleTableSort}
            />
          )}

          {/* City Column */}
          {headerConfig.showCity && (
            <HeaderColumn label="City" showInfo tooltipId="city" />
          )}

          {/* Update URL Button for Universities */}
          {headerConfig.showUpdateURLButton && (
            <UpdateURLButton
              loading={loading}
              urlUpdateSuccess={urlUpdateSuccess}
              onClick={handleUpdateURLs}
            />
          )}

          {/* Name/City Name Column */}
          <SortableHeaderColumn
            label={headerConfig.showCityName ? "City Name" : "Name"}
            columnName="Name"
            sortOption={sortOption}
            onSort={handleTableSort}
          />

          {/* Type Column (Cities only) */}
          {headerConfig.showType && showConfirmed && (
            <HeaderColumn label="Type" />
          )}

          {/* Action Column (Cities only) */}
          {headerConfig.showAction && <HeaderColumn label="Action" />}

          {/* Count Column (Cities) */}
          {headerConfig.showCount && headerConfig.showCityName && (
            <SortableHeaderColumn
              label="Count"
              columnName="Count"
              sortOption={sortOption}
              onSort={handleTableSort}
            />
          )}

          {/* Tags Column */}
          {headerConfig.showTags && <HeaderColumn label="Tags" />}

          {/* State Column */}
          {headerConfig.showState && (
            <SortableHeaderColumn
              label="State"
              columnName="State"
              sortOption={sortOption}
              onSort={handleTableSort}
            />
          )}

          {/* Nearest City Column */}
          {headerConfig.showNearestCity && (
            <HeaderColumn label="Nearest City" />
          )}
        </tr>
      </thead>

      {/* MODALS */}
      <ConfirmModal
        type="info"
        open={openConfirmationBox}
        setOpen={setOpenConfirmationBox}
        title="Confirm Companies"
        description={`Confirm Selected ${selected} in chosen Cities?`}
        onConfirm={handleMultiConfirm}
      />

    </>
  );
}

export default TableHeader;
